// authSlice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'
import { toast } from 'react-hot-toast';
import { url } from '../services/api'

const MFA_TRUST_TOKEN_KEY = 'mfaTrustToken';

const setSessionValue = (key, value) => {
    if (value === undefined || value === null || value === '') {
        sessionStorage.removeItem(key);
        return;
    }

    sessionStorage.setItem(key, value);
};

const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');

    return token ? { Authorization: `Bearer ${token}` } : {};
};

const persistEmployeeSession = (employeeData) => {
    if (employeeData?.mfaTrustToken) {
        localStorage.setItem(MFA_TRUST_TOKEN_KEY, employeeData.mfaTrustToken);
    }

    if (!employeeData?.token) {
        return;
    }

    setSessionValue('token', employeeData.token);
    setSessionValue('category', employeeData.productCategory);
    setSessionValue('name', employeeData.name);
    setSessionValue('email', employeeData.email);
};

const getErrorMessage = (error, fallback = 'Something went wrong') => {
    return error?.response?.data?.message || error?.message || fallback;
};

const initialState = {
    token: sessionStorage.getItem('token'),
    name: sessionStorage.getItem('name'),
    email: sessionStorage.getItem('email'),
    role: '',
    _id: '',
    category: sessionStorage.getItem('category'),
};

export const loginemployee = createAsyncThunk(
    "employee/loginemployee",
    async (employee, { rejectWithValue }) => {
        try {
            const mfaTrustToken = localStorage.getItem(MFA_TRUST_TOKEN_KEY);
            const res = await axios.post(`${url}/api/login`, {
                email: employee.email,
                password: employee.password,
                role: employee.role,
                mfaTrustToken,
            });

            if (res.status === 200) {
                if (res.data?.token) {
                    persistEmployeeSession(res.data);
                    toast.success("Login Successful");
                } else if (res.data?.mfaSetupRequired) {
                    toast.success(res.data?.message || "MFA setup is required");
                } else if (res.data?.mfaRequired) {
                    toast.success(res.data?.message || "MFA verification required");
                }
            }

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error);
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const startMfaSetup = createAsyncThunk(
    "employee/startMfaSetup",
    async ({ setupToken } = {}, { rejectWithValue }) => {
        try {
            const bodyData = setupToken ? { setupToken } : {};
            const res = await axios.post(`${url}/api/mfa/setup/start`, bodyData, {
                headers: getAuthHeaders(),
            });

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to start MFA setup');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const verifyMfaSetup = createAsyncThunk(
    "employee/verifyMfaSetup",
    async ({ setupToken, code }, { rejectWithValue }) => {
        try {
            const bodyData = setupToken ? { setupToken, code } : { code };
            const res = await axios.post(`${url}/api/mfa/setup/verify`, bodyData, {
                headers: getAuthHeaders(),
            });

            if (res.status === 200) {
                persistEmployeeSession(res.data);
                toast.success("MFA enabled successfully");
            }

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to verify MFA setup');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const verifyMfaLogin = createAsyncThunk(
    "employee/verifyMfaLogin",
    async ({ mfaToken, code }, { rejectWithValue }) => {
        try {
            const res = await axios.post(`${url}/api/login/mfa/verify`, {
                mfaToken,
                code,
            });

            if (res.status === 200 && res.data?.token) {
                persistEmployeeSession(res.data);
                toast.success("Login Successful");
            }

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to verify MFA login');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const verifyMfaRecoveryCode = createAsyncThunk(
    "employee/verifyMfaRecoveryCode",
    async ({ mfaToken, recoveryCode }, { rejectWithValue }) => {
        try {
            const res = await axios.post(`${url}/api/login/mfa/recovery/verify`, {
                mfaToken,
                recoveryCode,
            });

            if (res.status === 200 && res.data?.token) {
                persistEmployeeSession(res.data);
                toast.success(res.data?.message || "Login Successful");
            }

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to verify recovery code');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const getMfaStatus = createAsyncThunk(
    "employee/getMfaStatus",
    async (_, { rejectWithValue }) => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(`${url}/api/mfa/status`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to fetch MFA status');
            return rejectWithValue(message);
        }
    }
)

export const disableMfa = createAsyncThunk(
    "employee/disableMfa",
    async ({ password, code }, { rejectWithValue }) => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.post(`${url}/api/mfa/disable`, {
                password,
                code,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            localStorage.removeItem(MFA_TRUST_TOKEN_KEY);
            toast.success(res.data?.message || "MFA disabled successfully");
            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to disable MFA');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

export const regenerateMfaRecoveryCodes = createAsyncThunk(
    "employee/regenerateMfaRecoveryCodes",
    async ({ password, code }, { rejectWithValue }) => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.post(`${url}/api/mfa/recovery-codes/regenerate`, {
                password,
                code,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            toast.success(res.data?.message || "Recovery codes regenerated successfully");
            return res.data;
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to regenerate recovery codes');
            toast.error(`Error : ${message}`);
            return rejectWithValue(message);
        }
    }
)

const applyTokenPayload = (state, payload) => {
    if (!payload?.token) {
        return;
    }

    const employee = jwtDecode(payload.token);
    state.token = payload.token;
    state.name = sessionStorage.getItem('name');
    state.email = sessionStorage.getItem('email');
    state._id = employee._id;
    state.role = employee.role;
    state.category = sessionStorage.getItem('category');
};

const employeeSlice = createSlice({
    name: 'employee',
    initialState,
    reducers: {
        loademployee(state, action) {
            const token = state.token
            if (token) {
                const employee = jwtDecode(token);
                return {
                    ...state,
                    token,
                    _id: employee._id,
                    role: employee.role,
                    category: sessionStorage.getItem('category'),
                };
            }
        },
        logoutemployee(state, action) {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('category');
            sessionStorage.removeItem('name');
            sessionStorage.removeItem('email');

            return {
                ...state,
                token: "",
                name: "",
                email: '',
                role: '',
                _id: '',
                category: null
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loginemployee.fulfilled, (state, action) => {
            applyTokenPayload(state, action.payload);
        });
        builder.addCase(verifyMfaSetup.fulfilled, (state, action) => {
            applyTokenPayload(state, action.payload);
        });
        builder.addCase(verifyMfaLogin.fulfilled, (state, action) => {
            applyTokenPayload(state, action.payload);
        });
        builder.addCase(verifyMfaRecoveryCode.fulfilled, (state, action) => {
            applyTokenPayload(state, action.payload);
        });
    }
});

export const { loademployee, logoutemployee } = employeeSlice.actions
export default employeeSlice.reducer
